import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { PageBackground } from "../../components/PageBackground";
import {
  MAGIC_LINK_SENT_MESSAGE,
  magicLinkErrorMessage,
  passwordSignInErrorMessage,
  shouldConfirmMagicLinkSent,
} from "../../utils/authHelpers";

const FIELD_LABEL_STYLE = { display:"block", fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:6 };
const FIELD_INPUT_STYLE = { width:"100%", boxSizing:"border-box", padding:"12px 14px", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:10, color:"#151515", fontSize:14, outline:"none" };

export function AuthScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordMode, setPasswordMode] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const { signIn, signInWithPassword, signInWithGoogle, isConfigured, authError, clearAuthError } = useAuth();

  const togglePasswordMode = () => {
    setError("");
    clearAuthError();
    setPassword("");
    setPasswordMode((prev) => !prev);
  };

  const handleGoogleSignIn = async () => {
    setError("");
    clearAuthError();
    setGoogleSubmitting(true);
    const { error: err } = await signInWithGoogle();
    if (err) {
      setError(err.message || "Something went wrong");
      setGoogleSubmitting(false);
    }
    // On success Supabase redirects — no need to reset state.
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    clearAuthError();
    setSubmitting(true);
    const { error: err } = await signIn(email);
    setSubmitting(false);
    if (err && !shouldConfirmMagicLinkSent(err)) {
      setError(magicLinkErrorMessage(err));
    } else {
      setSent(true);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    clearAuthError();
    setSubmitting(true);
    try {
      const { error: err } = await signInWithPassword(email.trim(), password);
      if (err) setError(passwordSignInErrorMessage(err));
    } catch (err) {
      // `?? {}` so a rejection with no reason still maps to the try-again message, not null.
      setError(passwordSignInErrorMessage(err ?? {}));
    } finally {
      setSubmitting(false);
    }
    // On success the AuthContext session listener routes away — nothing else to do here.
  };

  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'DM Sans','Helvetica Neue',sans-serif", color:"#151515", display:"flex", flexDirection:"column", position:"relative", overflow:"hidden" }}>
      <PageBackground />
      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:24, minHeight:0, position:"relative", zIndex:1 }}>
        <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:20, width:"100%", maxWidth:400, padding:40, boxShadow:"0 3px 15px rgba(0,0,0,0.08)" }}>
          {!isConfigured && (
            <p style={{ background:"#FFF5FB", border:"1px solid #F9009133", borderRadius:8, padding:12, marginBottom:20, fontSize:13, color:"#151515" }}>
              Supabase not loaded. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to <strong>.env</strong> (not .env.example), then restart the dev server.
            </p>
          )}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
            <span style={{
              display:"inline-flex", alignItems:"center", gap:6,
              background:"linear-gradient(135deg, #FFF5FB 0%, #F0F9FF 100%)",
              border:"1px solid #F9009133",
              borderRadius:20, padding:"5px 12px",
              fontSize:11, fontWeight:600, color:"#F90091",
              letterSpacing:0.3,
            }}>
              <span style={{ fontSize:13 }}>✨</span>
              Check out our new look
            </span>
          </div>
          <h1 style={{ fontSize:28, fontWeight:600, color:"#151515", marginBottom:8, textAlign:"center", fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Sign in</h1>
          <p style={{ color:"#626262", fontSize:14, marginBottom:28, textAlign:"center" }}>Sign in to your account</p>
          {sent ? (
            <p style={{ color:"#208a3e", fontSize:14, textAlign:"center" }}>{MAGIC_LINK_SENT_MESSAGE}</p>
          ) : (
            <>
              {authError && !error && (
                <p style={{ background:"#FFF5FB", border:"1px solid #F9009133", borderRadius:8, padding:12, marginBottom:20, fontSize:13, color:"#151515", textAlign:"center" }}>
                  {authError}
                </p>
              )}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleSubmitting || !isConfigured}
                style={{ width:"100%", padding:"13px 0", background:"#fff", color:"#151515", border:"1px solid #E2E2E2", borderRadius:10, fontSize:15, fontWeight:600, cursor: googleSubmitting || !isConfigured ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:20 }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                {googleSubmitting ? "Redirecting…" : "Continue with Google"}
              </button>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <div style={{ flex:1, height:1, background:"#E2E2E2" }} />
                <span style={{ fontSize:12, color:"#B8B8B8" }}>or</span>
                <div style={{ flex:1, height:1, background:"#E2E2E2" }} />
              </div>
            <form onSubmit={passwordMode ? handlePasswordSubmit : handleSubmit}>
              <div style={{ marginBottom:20 }}>
                <label htmlFor="auth-email" style={FIELD_LABEL_STYLE}>Email</label>
                <input
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  style={FIELD_INPUT_STYLE}
                />
              </div>
              {passwordMode && (
                <div style={{ marginBottom:20 }}>
                  <label htmlFor="auth-password" style={FIELD_LABEL_STYLE}>Password</label>
                  <input
                    id="auth-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    style={FIELD_INPUT_STYLE}
                  />
                </div>
              )}
              {error && <p style={{ color:"#e53935", fontSize:13, marginBottom:12 }}>{error}</p>}
              <button type="submit" disabled={submitting || !isConfigured} style={{ width:"100%", padding:"13px 0", background: isConfigured ? "#151515" : "#E2E2E2", color: isConfigured ? "#fff" : "#B8B8B8", border:"none", borderRadius:10, fontSize:15, fontWeight:600, cursor: submitting || !isConfigured ? "not-allowed" : "pointer" }}>
                {passwordMode
                  ? (submitting ? "Signing in…" : "Sign in")
                  : (submitting ? "Sending…" : "Send magic link")}
              </button>
              <button
                type="button"
                onClick={togglePasswordMode}
                style={{ width:"100%", background:"transparent", border:"none", color:"#626262", fontSize:12, marginTop:14, cursor:"pointer", textDecoration:"underline" }}
              >
                {passwordMode ? "Email me a sign-in link instead" : "Sign in with password"}
              </button>
            </form>
            </>
          )}
        </div>
      </div>

      <div style={{ flexShrink:0, textAlign:"center", padding:"36px 0 28px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, position:"relative", zIndex:1 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:18, color:"rgba(98,98,98,0.7)", fontFamily:"'DM Sans',sans-serif" }}>Powered by</span>
        <svg width="126" height="20" viewBox="0 0 973.7 146.9" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity:0.5 }}>
          <path fill="#151515" d="M7.6,59.2c-4.2,0-7.6,3.5-7.6,7.6v16.7c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6v-16.7c0-4.2-3.4-7.6-7.6-7.6Z"/>
          <path fill="#151515" d="M174.6,59.2h0c4.2,0,7.6,3.4,7.6,7.6v16.6c0,4.2-3.4,7.6-7.6,7.6h0c-4.2,0-7.6-3.4-7.6-7.6v-16.6c0-4.2,3.4-7.6,7.6-7.6Z"/>
          <path fill="#151515" d="M39.1,34.1c-4.2,0-7.6,3.4-7.6,7.6v67c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6V41.7c0-4.2-3.4-7.6-7.6-7.6Z"/>
          <path fill="#151515" d="M143,34.1c-4.2,0-7.6,3.4-7.6,7.6v67c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6V41.7c0-4.2-3.4-7.6-7.6-7.6Z"/>
          <path fill="#151515" d="M104.9,3.7c-4.1-2.4-8.8-3.7-13.9-3.7s-9.7,1.3-13.8,3.7c-8.4,4.8-14,13.8-14,24.2v111.5c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6v-50.6h25.5v50.6c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6V27.9c0-10.4-5.7-19.4-14-24.2h-.2ZM103.8,73.6h-25.5V27.9c0-7,5.7-12.7,12.7-12.7s12.7,5.7,12.7,12.7v45.7h.1Z"/>
          <rect fill="#151515" x="506.6" y="32.4" width="13.7" height="82" rx="6.6" ry="6.6"/>
          <path fill="#151515" d="M390.4,32.4h-.6c-3.6,0-6.6,2.9-6.6,6.6v39.8c0,4.6-2,11.2-5.8,15.3-3.4,3.6-9,6.6-16.5,6.6s-13.2-3.1-16.6-6.6c-3.8-4.1-5.8-9-5.8-15.3v-39.8c0-3.6-2.9-6.6-6.6-6.6h-.6c-3.6,0-6.6,2.9-6.6,6.6v37.4c0,9.7,1.9,17.8,7.4,24.7,5.3,6.7,16.5,13.2,28.8,13.2s23.3-6.5,28.7-13.2c5.5-6.9,7.4-15,7.4-24.7v-37.4c0-3.6-2.9-6.6-6.6-6.6h0Z"/>
          <path fill="#151515" d="M448.5,34.4h-23.9c-4.3,0-7.8,3.5-7.8,7.8v62.5c0,4.3,3.5,7.8,7.8,7.8h23.9c7.3,0,20.4-.5,29.9-14.2,4-5.8,8-13.2,8-24.8s-4-19-8-24.8c-9.5-13.7-22.6-14.2-29.9-14.2h0ZM468.6,88.5c-3.4,5.9-8.6,11.3-19,11.3h-19.1v-52.8h19.1c10.4,0,15.6,5.4,19,11.3,3.2,5.6,4,10,4,15s-.8,9.4-4,15v.2Z"/>
          <path fill="#151515" d="M697,32.5h-.6c-3.6,0-6.6,2.9-6.6,6.6v28.8h-39v-28.8c0-3.6-2.9-6.6-6.6-6.6h-.6c-3.6,0-6.6,2.9-6.6,6.6v68.8c0,3.6,2.9,6.6,6.6,6.6h.6c3.6,0,6.6-2.9,6.6-6.6v-26.3h39v26.3c0,3.6,2.9,6.6,6.6,6.6h.6c3.6,0,6.6-2.9,6.6-6.6V39.1c0-3.6-2.9-6.6-6.6-6.6Z"/>
          <path fill="#151515" d="M277.6,36.2c-1.2-2.4-3.6-3.8-6.2-3.8s-5,1.4-6.2,3.8l-34.2,68.2c-1.7,3.4-.3,7.5,3.1,9.2,3.4,1.7,7.5.3,9.2-3.1,0,0,2.8-5.6,6.5-12.9h43.2c3.7,7.3,6.5,12.9,6.5,12.9,1.7,3.4,5.8,4.8,9.2,3.1h0c3.4-1.7,4.7-5.8,3-9.2l-34.2-68.2h.1ZM256.1,85.2c4.9-9.7,9.5-18.9,10.5-20.9,1.4-2.9,3.6-7.5,4.9-10.4,1.3,2.9,3.4,7.5,4.9,10.4,1,2,5.6,11.2,10.5,20.9h-30.8Z"/>
          <path fill="#151515" d="M577.7,32.5c-24.2,0-39.9,17.3-39.9,41s14.9,41,39.9,41,39.9-19.7,39.9-41-15.6-41-39.9-41ZM577.9,100.5c-16.2,0-25.8-13-25.8-27.1s10.1-27.1,25.8-27.1,25.8,11.4,25.8,27.1-9.6,27.1-25.8,27.1Z"/>
          <path fill="#151515" d="M759.1,32.5c-24.2,0-39.9,17.3-39.9,41s14.9,41,39.9,41,39.9-19.7,39.9-41-15.6-41-39.9-41ZM759.3,100.5c-16.2,0-25.8-13-25.8-27.1s10.1-27.1,25.8-27.1,25.8,11.4,25.8,27.1-9.6,27.1-25.8,27.1Z"/>
          <path fill="#151515" d="M850.8,32.5c-24.2,0-39.9,17.3-39.9,41s14.9,41,39.9,41,39.9-19.7,39.9-41-15.6-41-39.9-41ZM851,100.5c-16.2,0-25.8-13-25.8-27.1s10.1-27.1,25.8-27.1,25.8,11.4,25.8,27.1-9.6,27.1-25.8,27.1Z"/>
          <path fill="#151515" d="M971.8,102.8l-31.5-32.7,28.5-25.7c2.8-2.5,3-6.9.5-9.7s-6.9-3-9.7-.5l-27.3,24.6c-2,1.8-7.1,6.8-11.3,10.9v-30.7c0-3.6-2.9-6.6-6.6-6.6h-.6c-3.6,0-6.6,2.9-6.6,6.6v68.8c0,3.6,2.9,6.6,6.6,6.6h.6c3.6,0,6.6-2.9,6.6-6.6v-20.4l9.4-8.4c9.3,9.9,31.6,33.3,31.6,33.3,2.6,2.7,7,2.8,9.7.2,2.7-2.6,2.8-7,.2-9.7h-.1Z"/>
        </svg>
        </div>
        <div style={{ display:"flex", gap:16 }}>
          <a href="https://www.audiohook.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:"rgba(98,98,98,0.7)", textDecoration:"none" }}>Privacy Policy</a>
          <a href="https://www.audiohook.com/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:"rgba(98,98,98,0.7)", textDecoration:"none" }}>Terms of Use</a>
        </div>
      </div>
    </div>
  );
}
